import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  Autocomplete,
} from '@mui/material';

import { SaveOutlined, CancelOutlined } from '@mui/icons-material';
import { extractErrorMessage } from '../utils/errorUtils';
import { useProjetStore } from '../stores/useProjetStore';
import { useClientStore } from '../stores/useClientStore';
import { ProjetRequestDTO, StatutProjet, Client } from '../types';
import AddressAutocomplete from '../components/AddressAutocomplete';

type ProjetFormData = ProjetRequestDTO;

const ProjetFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const { addProjet, updateProjet, getProjetById, isLoading, error } = useProjetStore();
  const { clients, loadClients } = useClientStore();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [codePostal, setCodePostal] = useState('');
  const [ville, setVille] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjetFormData>({
    defaultValues: {
      titre: '',
      description: '',
      adresseChantier: '',
      clientId: 0,
      statut: StatutProjet.EN_ATTENTE_DEVIS,
      dateDebut: '',
      dateFin: '',
    },
  });

  // Charger les clients
  useEffect(() => {
    if (clients.length === 0) {
      loadClients();
    }
  }, [clients.length, loadClients]);

  // Charger le projet en mode édition
  useEffect(() => {
    if (isEditing && id) {
      const projetId = parseInt(id, 10);
      const projet = getProjetById(projetId);
      if (projet) {
        // Parse l'adresse complète pour extraire les composants
        const adresseMatch = projet.adresseChantier.match(/^(.+?),?\s*(\d{5})\s+(.+)$/);
        if (adresseMatch) {
          setCodePostal(adresseMatch[2]);
          setVille(adresseMatch[3]);
          reset({
            titre: projet.titre,
            description: projet.description || '',
            adresseChantier: adresseMatch[1].trim(),
            clientId: projet.clientId,
            statut: projet.statut,
            dateDebut: projet.dateDebut || '',
            dateFin: projet.dateFin || '',
          });
        } else {
          reset({
            titre: projet.titre,
            description: projet.description || '',
            adresseChantier: projet.adresseChantier,
            clientId: projet.clientId,
            statut: projet.statut,
            dateDebut: projet.dateDebut || '',
            dateFin: projet.dateFin || '',
          });
        }
      }
    }
  }, [isEditing, id, getProjetById, reset]);

  const onSubmit = async (data: ProjetFormData) => {
    setSubmitError(null);
    try {
      // Construire l'adresse complète avec code postal et ville
      const adresseComplete = codePostal && ville
        ? `${data.adresseChantier}, ${codePostal} ${ville}`
        : data.adresseChantier;

      const projetData = {
        ...data,
        adresseChantier: adresseComplete,
      };

      if (isEditing && id) {
        await updateProjet(parseInt(id, 10), projetData);
      } else {
        await addProjet(projetData);
      }
      navigate('/projets');
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Erreur lors de l\'enregistrement du projet');
      setSubmitError(errorMessage);
    }
  };

  const statutOptions = [
    { value: StatutProjet.EN_ATTENTE_DEVIS, label: 'En attente devis' },
    { value: StatutProjet.DEVIS_ENVOYE, label: 'Devis envoyé' },
    { value: StatutProjet.DEVIS_ACCEPTE, label: 'Devis accepté' },
    { value: StatutProjet.EN_COURS, label: 'En cours' },
    { value: StatutProjet.TERMINE, label: 'Terminé' },
    { value: StatutProjet.ANNULE, label: 'Annulé' },
  ];

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 700, color: '#333', mb: 3 }}
      >
        {isEditing ? 'Modifier le projet' : 'Nouveau projet'}
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {(submitError || error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError || error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={{ xs: 3, sm: 2 }}>
            {/* Titre du projet */}
            <Grid item xs={12}>
              <Controller
                name="titre"
                control={control}
                rules={{ required: 'Le titre est requis' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Titre du projet"
                    error={!!errors.titre}
                    helperText={errors.titre?.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Client */}
            <Grid item xs={12}>
              <Controller
                name="clientId"
                control={control}
                rules={{
                  required: 'Le client est requis',
                  validate: (value) => value > 0 || 'Veuillez sélectionner un client'
                }}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(option) => `${option.prenom} ${option.nom}`}
                    value={clients.find((c) => c.id === value) || null}
                    onChange={(_, newValue: Client | null) => {
                      onChange(newValue ? newValue.id : 0);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Client"
                        error={!!errors.clientId}
                        helperText={errors.clientId?.message}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                          },
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Adresse du chantier */}
            <Grid item xs={12}>
              <Controller
                name="adresseChantier"
                control={control}
                rules={{ required: "L'adresse du chantier est requise" }}
                render={({ field }) => (
                  <AddressAutocomplete
                    label="Adresse du chantier *"
                    value={field.value}
                    onChange={field.onChange}
                    onAddressSelect={(address) => {
                      field.onChange(address.adresse);
                      setCodePostal(address.codePostal);
                      setVille(address.ville);
                    }}
                    error={!!errors.adresseChantier}
                    helperText={errors.adresseChantier?.message}
                    placeholder="Ex: 8 Boulevard du Port"
                    required
                  />
                )}
              />
            </Grid>

            {/* Code Postal et Ville */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code postal *"
                value={codePostal}
                onChange={(e) => setCodePostal(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ville *"
                value={ville}
                onChange={(e) => setVille(e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Description (optionnel)"
                    multiline
                    rows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {/* Statut */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="statut"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Statut"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  >
                    {statutOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {/* Dates */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateDebut"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date de début (optionnel)"
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="dateFin"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Date de fin (optionnel)"
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: { xs: 3, sm: 4 } }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />}
              sx={{
                py: { xs: 1.8, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                bgcolor: '#F59E0B',
                '&:hover': { bgcolor: '#D97706' },
              }}
            >
              {isLoading ? 'Enregistrement...' : (isEditing ? 'Modifier' : 'Enregistrer')}
            </Button>

            <Button
              variant="outlined"
              size="large"
              fullWidth
              startIcon={<CancelOutlined />}
              onClick={() => navigate('/projets')}
              sx={{
                py: { xs: 1.8, sm: 1.5 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
              }}
            >
              Annuler
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ProjetFormPage;
